import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Copy, ExternalLink, MessageCircle, ShieldAlert, X } from 'lucide-react';
import { apiClient } from '../../api';
import { monoLabel, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle } from '../UI/ink';
import { IOC_TYPE_LABEL, KILL_CHAIN, SEV_NAME, sevBg, sevFg, sourceLabel, stamp } from './model';
import VerdictBadge from '../Triage/VerdictBadge';

const REF_KIND = { ADVISORY: 'ADVISORY', FIX: 'FIX', REPORT: 'REPORT', WEB: 'LINK', PACKAGE: 'PACKAGE', ARTICLE: 'ARTICLE' };

// OSV lookups are shared across selections so re-selecting a CVE is instant.
const osvCache = {};

const Label = ({ children, right }) => (
  <span className="flex items-center justify-between gap-3">
    <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>{children}</span>
    {right}
  </span>
);

const Card = ({ children, className = '' }) => (
  <div className={`flex flex-col gap-2 px-3.5 py-3 rounded-[10px] border min-w-0 ${className}`} style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>{children}</div>
);

const SevChip = ({ sev, big }) => (
  <span className={`inline-flex items-center gap-1.5 ${big ? 'h-6 px-[9px] text-[11px]' : 'h-[22px] px-2 text-[10.5px]'} rounded-md font-mono font-medium tracking-[0.06em]`} style={{ background: sevBg(sev) }}>
    <span className={big ? 'w-[7px] h-[7px] rounded-[1px]' : 'w-1.5 h-1.5 rounded-[1px]'} style={{ background: sevFg(sev) }} />
    {SEV_NAME[sev]}
  </span>
);
export { SevChip };

const Timeline = ({ rows }) => (
  <div className="flex flex-col gap-2">
    <Label>ASSET &amp; TIMELINE</Label>
    {rows.map(([t, what]) => (
      <div key={what} className="flex items-baseline gap-3 text-[13px]">
        <span className="w-[108px] shrink-0 font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{t}</span>
        <span style={{ color: 'var(--ink-muted)' }}>{what}</span>
      </div>
    ))}
  </div>
);

const iocStory = (a, host) => {
  const v = a.ioc_value;
  const stage = a.kill_chain_stage;
  switch (a.ioc_type) {
    case 'ip': return `${host} established a network connection to IP address ${v}, identified in threat intelligence feeds as a malicious host${stage ? ` associated with ${stage}` : ''}.`;
    case 'domain': return `${host} resolved or connected to the domain ${v}, which threat intelligence feeds list as malicious${stage ? ` (${stage})` : ''}.`;
    case 'hash': return `A file matching hash ${v} was detected on ${host}. This hash matches a known malicious file tracked in threat intelligence feeds.`;
    case 'url': return `${host} made an HTTP request to ${v}, which threat intelligence feeds flag as a malicious URL${stage ? ` (${stage})` : ''}.`;
    case 'filename': return `A file named "${v}" was found on ${host}, matching a known malicious filename in threat intelligence feeds.`;
    default: return `Indicator "${v}" was detected on ${host} and matched a threat intelligence entry.`;
  }
};

const IocBody = ({ item, assetCount, onResolve, busy }) => {
  const a = item.raw;
  const [copied, setCopied] = useState(false);
  const stageIdx = KILL_CHAIN.findIndex(([n]) => n === a.kill_chain_stage);
  const timeline = [[stamp(a.detected_at), 'Matched against threat intelligence at ingest']];
  if (assetCount > 1) timeline.push(['', `Same indicator seen on ${assetCount} assets`]);
  if (a.resolved_at) timeline.push([stamp(a.resolved_at), 'Marked resolved']);

  return (
    <div className="flex flex-col gap-[18px]">
      {item.fp && (
        <div className="flex flex-col gap-2.5 px-3.5 py-3 rounded-[10px] border border-dashed" style={{ borderColor: 'var(--surf-line-strong)' }}>
          <span className="text-[13.5px] font-semibold">Likely a false positive</span>
          <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>
            {a.ioc_value} is a private address on your own network. Feeds sometimes list private ranges by mistake.
          </span>
          <button type="button" onClick={onResolve} disabled={busy} className="self-start h-9 px-3 rounded-lg border text-[13px] font-medium disabled:opacity-50" style={secondaryBtnStyle}>
            Resolve as false positive
          </button>
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <Label>WHAT HAPPENED</Label>
        <p className="m-0 text-sm leading-relaxed">{iocStory(a, item.host)}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card>
          <span className="font-mono text-[10px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>INDICATOR</span>
          <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{IOC_TYPE_LABEL[a.ioc_type] || a.ioc_type}</span>
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="font-mono text-[13px] truncate" title={a.ioc_value}>{a.ioc_value}</span>
            <button
              type="button"
              aria-label={copied ? 'Copied' : 'Copy indicator'}
              title={copied ? 'Copied' : 'Copy indicator'}
              onClick={() => { navigator.clipboard.writeText(a.ioc_value); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              className="w-[26px] h-[26px] shrink-0 flex items-center justify-center"
              style={{ color: copied ? 'var(--sev-good-fg)' : 'var(--ink-faint)' }}
            >
              <Copy className="w-[13px] h-[13px]" strokeWidth={1.8} />
            </button>
          </span>
        </Card>
        <Card>
          <span className="font-mono text-[10px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>THREAT INTELLIGENCE</span>
          <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{a.source ? `${sourceLabel(a)} reference` : 'Feed reference'}</span>
          <span className="font-mono text-[13px] truncate" title={a.source_event_id}>{a.source_event_id || '—'}</span>
          {a.description && <span className="text-xs leading-snug" style={{ color: 'var(--ink-muted)' }}>{a.description}</span>}
        </Card>
      </div>
      <div className="flex flex-col gap-2.5">
        <Label right={<span className="font-mono text-[11px]" style={{ color: 'var(--ink-muted)' }}>{a.attack_technique || 'No technique reported'}</span>}>ATTACK CONTEXT</Label>
        <div className="grid grid-cols-7 gap-[3px]">
          {KILL_CHAIN.map(([name, short], i) => (
            <div key={name} className="flex flex-col items-center gap-1.5" title={name}>
              <span className="w-full h-2 rounded-sm" style={{ background: stageIdx < 0 ? 'var(--surf-raised)' : i === stageIdx ? sevFg(item.sev) : i < stageIdx ? 'var(--surf-line-strong)' : 'var(--surf-raised)' }} />
              <span className="font-mono text-[10px]" style={{ color: i === stageIdx ? 'var(--ink-text)' : 'var(--ink-faint)', fontWeight: i === stageIdx ? 600 : 400 }}>{short}</span>
            </div>
          ))}
        </div>
        <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>
          {stageIdx < 0 ? 'The feed didn’t report a kill chain stage.' : `Lockheed Martin kill chain stage reported by the feed: ${a.kill_chain_stage}.`}
        </span>
      </div>
      <Timeline rows={timeline} />
    </div>
  );
};

const CveBody = ({ item }) => {
  const a = item.raw;
  const [osv, setOsv] = useState(osvCache[a.cve_id] || null);

  useEffect(() => {
    let live = true;
    if (osvCache[a.cve_id]) { setOsv(osvCache[a.cve_id]); return undefined; }
    setOsv(null);
    apiClient.get(`/api/vulns/osv?cve_id=${encodeURIComponent(a.cve_id)}`)
      .then(r => { osvCache[a.cve_id] = r.data; if (live) setOsv(r.data); })
      .catch(() => { osvCache[a.cve_id] = { found: false }; if (live) setOsv({ found: false }); });
    return () => { live = false; };
  }, [a.cve_id]);

  const score = a.cvss_score != null ? Number(a.cvss_score) : null;
  const timeline = [[stamp(a.detected_at), 'Matched installed package against NVD']];
  if (a.resolved_at) timeline.push([stamp(a.resolved_at), 'Marked resolved']);

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex items-center gap-4 px-4 py-3.5 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
        <div className="flex flex-col gap-0.5">
          <span className="font-heading text-4xl font-semibold leading-none">{score != null ? score.toFixed(1) : '—'}</span>
          <span className="font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}>CVSS</span>
        </div>
        <div className="flex-grow min-w-0 flex flex-col gap-2">
          <span className="h-2 rounded flex" style={{ background: 'var(--surf-raised)' }}>
            <span className="h-2 rounded" style={{ width: `${(score || 0) * 10}%`, background: sevFg(item.sev) }} />
          </span>
          <span className="font-mono text-[11px] truncate" style={{ color: 'var(--ink-muted)' }}>
            {a.severity} severity{a.ecosystem ? ` · ${a.ecosystem}` : ''}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label>PACKAGE &amp; FIX</Label>
        <div className="flex items-center gap-2.5 px-3.5 py-3 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
          <span className="flex-grow min-w-0 flex flex-col gap-[3px]">
            <span className="font-mono text-[13px] truncate">{a.package_name}</span>
            <span className="font-mono text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>installed {a.version}{a.ecosystem ? ` · ${a.ecosystem}` : ''}</span>
          </span>
          <ArrowRight className="w-4 h-4 shrink-0" style={{ color: 'var(--ink-faint)' }} strokeWidth={1.8} />
          <span className="flex flex-col gap-[3px] items-end shrink-0">
            {!osv ? (
              <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Checking OSV…</span>
            ) : osv.fix_version ? (
              <>
                <span className="flex items-center gap-1.5 font-mono text-[13px]"><span className="w-[7px] h-[7px] rounded-full" style={{ background: 'var(--sev-good-fg)' }} />{osv.fix_version}</span>
                <span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>fix available · OSV</span>
              </>
            ) : (
              <span className="text-xs text-right" style={{ color: 'var(--ink-faint)' }}>{osv.found === false ? 'No OSV entry' : 'No fix released yet'}</span>
            )}
          </span>
        </div>
        {osv?.affected_range && <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Affects <span className="font-mono">{osv.affected_range}</span></span>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>DESCRIPTION</Label>
        <p className="m-0 text-sm leading-relaxed" style={{ color: a.description ? 'var(--ink-text)' : 'var(--ink-faint)' }}>{a.description || 'No description available.'}</p>
      </div>

      <div className="flex flex-col gap-2">
        <Label>REFERENCES</Label>
        <div className="flex flex-wrap gap-2">
          <a href={`https://nvd.nist.gov/vuln/detail/${a.cve_id}`} target="_blank" rel="noopener noreferrer" className="h-8 inline-flex items-center gap-2 px-2.5 rounded-lg border text-[12.5px] no-underline" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-link)' }}>
            <span className="font-mono text-[10px] tracking-[0.08em]" style={{ color: 'var(--ink-faint)' }}>NVD</span>{a.cve_id}<ExternalLink className="w-3 h-3" />
          </a>
          {(osv?.references || []).slice(0, 6).map((ref, i) => {
            let host = '';
            try { host = new URL(ref.url).hostname.replace(/^www\./, ''); } catch { host = 'link'; }
            return (
              <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="h-8 inline-flex items-center gap-2 px-2.5 rounded-lg border text-[12.5px] no-underline max-w-full" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-link)' }}>
                <span className="font-mono text-[10px] tracking-[0.08em]" style={{ color: 'var(--ink-faint)' }}>{REF_KIND[ref.type] || 'LINK'}</span>
                <span className="truncate">{host}</span><ExternalLink className="w-3 h-3 shrink-0" />
              </a>
            );
          })}
        </div>
      </div>
      <Timeline rows={timeline} />
    </div>
  );
};

const AlertDetail = ({ item, assetCount, busy, onClose, onResolve, onCreateCase, onDiscuss, verdict, canRate, onVerdictChange }) => {
  if (!item) {
    return (
      <aside aria-label="Alert details" className="h-full min-h-[520px] rounded-[14px] border flex flex-col items-center justify-center gap-2.5 p-8 text-center" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
        <ShieldAlert className="w-7 h-7" style={{ color: 'var(--ink-faint)' }} strokeWidth={1.6} />
        <span className="text-[15px] font-semibold">Select an alert</span>
        <span className="text-[13px] leading-normal max-w-[280px]" style={{ color: 'var(--ink-muted)' }}>What happened, the indicator or package, and how to fix it will show here.</span>
      </aside>
    );
  }
  const a = item.raw;
  const isIoc = item.kind === 'ioc';
  const kicker = isIoc
    ? `IOC MATCH · ${(IOC_TYPE_LABEL[a.ioc_type] || a.ioc_type || '').toUpperCase()}`
    : 'VULNERABILITY · NVD + OSV';
  const heading = isIoc ? item.title : `${a.cve_id} — ${a.package_name} ${a.version}`;
  const tags = isIoc
    ? [a.source && sourceLabel(a).toUpperCase(), a.kill_chain_stage && a.kill_chain_stage.toUpperCase()].filter(Boolean)
    : [a.package_name];

  return (
    <aside aria-label="Alert details" className="h-full min-h-[520px] rounded-[14px] border flex flex-col overflow-hidden" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
      <div className="flex flex-col gap-2.5 px-6 pt-5 pb-4 border-b" style={{ borderColor: 'var(--surf-line)' }}>
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-link)' }}>{kicker}</span>
          <button type="button" onClick={onClose} aria-label="Close details" className="w-8 h-8 flex items-center justify-center rounded-lg border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
            <X className="w-[13px] h-[13px]" strokeWidth={2} />
          </button>
        </div>
        <h2 className="m-0 font-heading text-[21px] font-semibold tracking-[-0.01em] leading-tight break-words">{heading}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <SevChip sev={item.sev} big />
          {tags.map(t => (
            <span key={t} className="h-6 inline-flex items-center px-2 rounded-md border font-mono text-[10.5px] tracking-[0.04em]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>{t}</span>
          ))}
          {item.resolved && (
            <span className="h-6 inline-flex items-center gap-1.5 px-2 rounded-md font-mono text-[10.5px] tracking-[0.04em]" style={{ background: 'var(--sev-good-bg)' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--sev-good-fg)' }} />RESOLVED
            </span>
          )}
        </div>
        {verdict && (
          <div className="mt-3">
            <div className="font-mono text-[10px] tracking-[0.08em] mb-1" style={{ color: 'var(--ink-faint)' }}>AI TRIAGE</div>
            <VerdictBadge verdict={verdict} interactive canRate={canRate} onChange={onVerdictChange} />
          </div>
        )}
      </div>

      <div className="flex-grow min-h-0 overflow-y-auto px-6 py-[18px]">
        {isIoc
          ? <IocBody item={item} assetCount={assetCount} onResolve={onResolve} busy={busy} />
          : <CveBody item={item} />}
      </div>

      <div className="flex flex-wrap items-center gap-2 px-6 py-4 border-t" style={{ borderColor: 'var(--surf-line)' }}>
        {!item.resolved && (
          <button type="button" onClick={onResolve} disabled={busy} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Resolving…' : 'Mark resolved'}</button>
        )}
        <button type="button" onClick={onCreateCase} className={secondaryBtn} style={secondaryBtnStyle}>Create case</button>
        <button type="button" onClick={onDiscuss} aria-label="Discuss with AI" title="Discuss with AI" className="w-11 h-11 flex items-center justify-center rounded-[10px] border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
          <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
        </button>
        <Link to={`/assets/${a.server_id}`} className="ml-auto h-11 flex items-center gap-1.5 px-1 text-sm no-underline truncate max-w-[45%]" style={{ color: 'var(--ink-link)' }}>
          Open {item.host}
        </Link>
      </div>
    </aside>
  );
};

export default AlertDetail;
